'use client';

import { useCallback, useState } from 'react';

export type ShowConfirmOptions = {
	message: string;
	title?: string;
	onConfirm?: () => void;
	onCancel?: () => void;
};

export type PopupConfirmContextType = {
	isOpen: boolean;
	message: string;
	title?: string;
	onConfirm?: () => void;
	onCancel?: () => void;
	show: (options: ShowConfirmOptions) => void;
	hide: () => void;
};

export const usePopupConfirmState = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [message, setMessage] = useState('');
	const [title, setTitle] = useState<string | undefined>();
	const [callbacks, setCallbacks] = useState<{
		onConfirm?: () => void;
		onCancel?: () => void;
	}>({});

	const show = useCallback(
		({ message, title, onConfirm, onCancel }: ShowConfirmOptions) => {
			setMessage(message);
			setTitle(title);
			setCallbacks({ onConfirm, onCancel });
			setIsOpen(true);
		},
		[],
	);

	const hide = useCallback(() => {
		setIsOpen(false);
		setMessage('');
		setTitle(undefined);
		setCallbacks({});
	}, []);

	const handleConfirm = useCallback(() => {
		callbacks.onConfirm?.();
		hide();
	}, [callbacks.onConfirm, hide]);

	const handleCancel = useCallback(() => {
		callbacks.onCancel?.();
		hide();
	}, [callbacks.onCancel, hide]);

	return {
		state: {
			isOpen,
			message,
			title,
		},
		actions: {
			show,
			hide,
			handleConfirm,
			handleCancel,
		},
	};
};
